import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "Marketplace", path: "/marketplace" },
  { label: "Vendor Dashboard", path: "/vendor-dashboard" },
  { label: "Product Listings", path: "/product-listings" },
  { label: "Overseas Warehouse", path: "/overseas-warehouse" },
  { label: "🚢 Freight Services", path: "/freight-services" },
  { label: "☀️ Solar & EV Hub", path: "/solar-ev" },
  { label: "Social Stars", path: "/social-stars" },
  { label: "Buyer Requirements & Quote", path: "/buyer-post" },
  { label: "Premium Ads", path: "/premium-ads" },
  { label: "💝 Community & Donate", path: "/community" },
  { label: "Membership", path: "/membership" },
];

const languages = [
  { code: "en", label: "English", flag: "🇺🇸", short: "EN" },
  { code: "zh", label: "中文", flag: "🇨🇳", short: "中文" },
  { code: "ar", label: "العربية", flag: "🇦🇪", short: "AR" },
  { code: "es", label: "Español", flag: "🇪🇸", short: "ES" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [activeLang, setActiveLang] = useState(languages[0]);
  const langRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selectLang = (lang: typeof languages[0]) => {
    setActiveLang(lang);
    setLangOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-sm" : "bg-transparent"
      }`}
    >
      {/* Top bar */}
      <div className={`border-b transition-all duration-300 ${scrolled ? "border-gray-100" : "border-white/10"}`}>
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 flex items-center justify-between h-9 text-xs">
          <div className={`flex items-center gap-4 ${scrolled ? "text-gray-500" : "text-white/80"}`}>
            <span className="flex items-center gap-1"><i className="ri-phone-line"></i> +86 186 8888 8888</span>
            <span className="hidden md:flex items-center gap-1"><i className="ri-mail-line"></i> trade@crownglobal.com</span>
          </div>
          <div className={`flex items-center gap-4 ${scrolled ? "text-gray-500" : "text-white/80"}`}>
            <span className="hidden sm:block">Free Shipping on Orders $500+</span>
            <span className="hidden md:block">|</span>
            <span className="hidden md:block">Trusted by 200K+ Suppliers</span>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img
            src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
            alt="CrownGlobal Logo"
            className="h-10 w-auto object-contain rounded"
          />
        </Link>

        {/* Search bar - desktop */}
        <div className="hidden lg:flex flex-1 mx-6 max-w-xl">
          <div className="flex w-full rounded-full overflow-hidden border border-amber-400 bg-white">
            <input
              type="text"
              placeholder="Search products, suppliers, categories..."
              className="flex-1 px-4 py-2 text-sm text-gray-700 outline-none bg-transparent"
            />
            <button className="bg-amber-400 hover:bg-amber-500 transition-colors px-5 flex items-center justify-center cursor-pointer">
              <i className="ri-search-line text-white text-base"></i>
            </button>
          </div>
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2">

          {/* Language switcher */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                scrolled
                  ? "border-gray-200 text-gray-600 hover:border-amber-400 hover:text-amber-600 bg-white"
                  : "border-white/30 text-white hover:bg-white/10 bg-white/10"
              }`}
            >
              <span className="text-sm">{activeLang.flag}</span>
              <span>{activeLang.short}</span>
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${langOpen ? "rotate-180" : ""}`}></i>
            </button>

            {langOpen && (
              <div className="absolute right-0 top-full mt-2 bg-white rounded-xl border border-gray-100 overflow-hidden z-50 min-w-[140px]" style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}>
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => selectLang(lang)}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors cursor-pointer text-left ${
                      activeLang.code === lang.code
                        ? "bg-amber-50 text-amber-600 font-bold"
                        : "text-gray-700 hover:bg-amber-50 hover:text-amber-600"
                    }`}
                  >
                    <span className="text-base">{lang.flag}</span>
                    <span className="font-medium">{lang.label}</span>
                    {activeLang.code === lang.code && (
                      <i className="ri-check-line text-amber-500 ml-auto text-xs"></i>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            className={`lg:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${scrolled ? "text-gray-700" : "text-white"}`}
            onClick={() => setSearchOpen(!searchOpen)}
          >
            <i className="ri-search-line text-lg"></i>
          </button>

          <Link
            to="/secure-payment"
            className="hidden md:flex items-center gap-1.5 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-shield-check-line"></i>
            <span>Secure Pay</span>
          </Link>

          <button className={`hidden md:flex items-center gap-1.5 text-sm font-medium cursor-pointer whitespace-nowrap ${scrolled ? "text-gray-700 hover:text-amber-500" : "text-white hover:text-amber-300"} transition-colors`}>
            <i className="ri-user-line text-base"></i>
            <span>Sign In</span>
          </button>

          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${scrolled ? "text-gray-700" : "text-white"}`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Nav links - desktop */}
      <div className={`hidden md:block border-t transition-all duration-300 ${scrolled ? "border-gray-100 bg-white" : "border-white/10 bg-black/20 backdrop-blur-sm"}`}>
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 flex items-center gap-1 h-10 overflow-x-auto">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                location.pathname === link.path
                  ? "bg-amber-400 text-white"
                  : scrolled
                  ? "text-gray-600 hover:text-amber-500 hover:bg-amber-50"
                  : "text-white/90 hover:text-white hover:bg-white/10"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Mobile search */}
      {searchOpen && (
        <div className="lg:hidden px-4 pb-3 bg-white border-b border-gray-100">
          <div className="flex rounded-full overflow-hidden border border-amber-400">
            <input
              type="text"
              placeholder="Search products, suppliers..."
              className="flex-1 px-4 py-2 text-sm text-gray-700 outline-none"
            />
            <button className="bg-amber-400 px-4 flex items-center justify-center cursor-pointer">
              <i className="ri-search-line text-white"></i>
            </button>
          </div>
        </div>
      )}

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 flex flex-col gap-1">
            {/* Language switcher in mobile menu */}
            <div className="flex gap-2 mb-2 pb-2 border-b border-gray-100">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setActiveLang(lang)}
                  className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-xl text-xs font-bold transition-colors cursor-pointer ${
                    activeLang.code === lang.code
                      ? "bg-amber-400 text-white"
                      : "bg-gray-50 text-gray-600 hover:bg-amber-50"
                  }`}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.short}</span>
                </button>
              ))}
            </div>

            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium px-4 py-3.5 rounded-xl transition-colors cursor-pointer flex items-center gap-2 ${
                  location.pathname === link.path
                    ? "bg-amber-400 text-white"
                    : "text-gray-700 hover:bg-amber-50 hover:text-amber-600"
                }`}
              >
                {link.label}
              </Link>
            ))}

            <div className="border-t border-gray-100 mt-2 pt-3 flex flex-col gap-2">
              <div className="flex gap-2">
                <button className="flex-1 bg-amber-400 text-white text-sm font-semibold py-3 rounded-xl cursor-pointer whitespace-nowrap">Sign In</button>
                <button className="flex-1 border border-amber-400 text-amber-500 text-sm font-semibold py-3 rounded-xl cursor-pointer whitespace-nowrap">Register</button>
              </div>
              <a
                href="https://wa.me/8618688888888?text=Hello%20Crown%20Global%2C%20I%20am%20interested%20in%20factory%20sourcing."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-green-500 text-white text-sm font-semibold py-3 rounded-xl cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg"></i> WhatsApp: +86 186 8888 8888
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
